import React, { useState } from "react";
import { Box, Typography, Paper, Grid } from "@mui/material";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import { DndContext, closestCenter } from "@dnd-kit/core";
import { SortableContext, rectSortingStrategy } from "@dnd-kit/sortable";
import { SortableDragOverlay } from "../../DragAndDrop/SortableDragOverlay";
import AddSection from "../../Components/Partials/AddSection/AddSection";
import { useCustomSensors } from "../../DragAndDrop/DragHandlers";
import { SortableChart } from "../StatisticsTest/components/SortableChart";
import { theme } from "../../Theme";
import UnknownMixChart from "../../Components/Charts/UnknownMixChart/UnknownMixChart";
import SKUShareChart from "src/Components/Charts/SkuShareByBrand/SKUShareChart";
import CumulativeProductShareChart from "src/Components/Charts/CumulativeProductShareChart/CumulativeProductShareChart";
import PPATable from "src/Components/Tables/PPATable/PPATable";


const data = [
  {
    id: "UnknownMixChart",
    component: <UnknownMixChart />,
    componentName: "UnknownMixChart",
    name: "Unknown Mix",
    xs: 12,
    md: 6,
  },
];

function Assortment() {
  const [activeId, setActiveId] = useState(null);
  const [isAddSectionOpen, setIsAddSectionOpen] = useState(false);

  const [items, setItems] = useState({
    mainDash: [
      {
        id: "SKUShareChart",
        component: <SKUShareChart />,
        componentName: "SKUShareChart",
        name: "SKUShareChart",
        xs: 6,
        md: 12,
      },
      {
        id: "CumulativeProductShareChart",
        component: <CumulativeProductShareChart />,
        componentName: "CumulativeProductShareChart",
        name: "CumulativeProductShareChart",
        xs: 6,
        md: 12,
      },
      // {
      //   id: "SmartMarketOpportunitiesTable",
      //   component: <SmartMarketOpportunitiesTable />,
      //   name: "SmartMarketOpportunitiesTable",
      //   xs: 6,
      //   md: 12,
      // },
      {
        id: "PPATable",
        component: <PPATable />,
        componentName: "PPATable",
        name: "PPATable",
        xs: 6,
        md: 12,
      },
    ],
    addSection: [...data],
  });

  const sensors = useCustomSensors();

  const handleDragStart = (event) => setActiveId(event.active.id);

  const handleDragEnd = (event) => {
    const { active, over } = event;

    // Ensure a valid drop target
    if (!over || active.id === over.id) {
      setActiveId(null);
      return;
    }

    setItems((prev) => {
      const fromMainDash = prev.mainDash.find((item) => item.id === active.id);
      const fromAddSection = prev.addSection.find(
        (item) => item.id === active.id
      );

      if (fromMainDash) {
        // Reorder within mainDash
        const updatedMainDash = [...prev.mainDash];
        const draggedItemIndex = updatedMainDash.findIndex(
          (i) => i.id === active.id
        );
        const overIndex = updatedMainDash.findIndex((i) => i.id === over.id);

        // Move item
        const [draggedItem] = updatedMainDash.splice(draggedItemIndex, 1);
        updatedMainDash.splice(overIndex, 0, draggedItem);

        return {
          ...prev,
          mainDash: updatedMainDash,
        };
      }

      if (fromAddSection) {
        // Move from addSection to mainDash
        const updatedAddSection = prev.addSection.filter(
          (i) => i.id !== active.id
        );
        const updatedMainDash = [...prev.mainDash, fromAddSection];

        return {
          mainDash: updatedMainDash,
          addSection: updatedAddSection,
        };
      }

      return prev;
    });

    setActiveId(null);
  };

  const toggleAddSection = () => setIsAddSectionOpen((prev) => !prev);

  const handleAddComponent = (id) => {
    setItems((prev) => {
      const item = prev.addSection.find((i) => i.id === id);
      if (!item) return prev;

      const updatedAddSection = prev.addSection.filter((i) => i.id !== id);
      const updatedMainDash = [...prev.mainDash, item];

      return {
        mainDash: updatedMainDash,
        addSection: updatedAddSection,
      };
    });
    setIsAddSectionOpen(false);
  };

  const TitleStyle = {
    fontWeight: theme.typography.fontWeightBold,
    fontSize: theme.typography.fontSize.medium,
    paddingY: 1,
    color: theme.palette.primary.main,
  };

  const ComponentBar = {
    backgroundColor: theme.palette.secondary.main,
    marginTop: "10px",
    padding: 1,
    display: "flex",
    flexDirection: "column",
    position: "relative",
  };

  const CloseButton = {
    alignSelf: "flex-end",
    padding: 0,
    margin: 0,
    color: theme.palette.primary.main,
    fontWeight: theme.typography.fontWeightBold,
    cursor: "pointer",
  };
  const RemoveComponent = (id) => {
    setItems((prev) => {
      const item = prev.mainDash.find((i) => i.id === id);
      const updatedMainDash = prev.mainDash.filter((i) => i.id !== id);
      const updatedAddSection = [...prev.addSection, item];

      return {
        mainDash: updatedMainDash,
        addSection: updatedAddSection,
      };
    });
  };
  return (
    <Box sx={{ mt: 2 }}>
      <AddSection sx={{ m: 2 }} toggleOpen={toggleAddSection} />
      {isAddSectionOpen && (
        <Box component={Paper} sx={ComponentBar}>
          <CloseOutlinedIcon sx={CloseButton} onClick={toggleAddSection} />
          <Box sx={{ display: "flex", flexDirection: "row" }}>
            {items.addSection.map((item) => (
              <Box
                key={item.id}
                onClick={() => handleAddComponent(item.id)}
                sx={{
                  cursor: "pointer",
                  padding: 2,
                  border: "1px solid #ccc",
                  margin: 1,
                  borderRadius: "4px",
                }}
              >
                <Typography>{item.name}</Typography>
              </Box>
            ))}
          </Box>
        </Box>
      )}

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
      >
        <SortableContext items={items.mainDash.map((i) => i.id)} strategy={rectSortingStrategy}>
          <Typography sx={TitleStyle}>Assortment & Distribution</Typography>
          <Grid
            container
            spacing={2}
            justifyContent="center"
            alignItems="center"
          >
            {items.mainDash.map((item) => (
              <Grid
                item
                xs={12}
                sm={8}
                md={item.md}
                key={item.id}
                style={{
                  marginTop: 10,
                }}
              >
                <SortableChart
                  RemoveComponent={() => RemoveComponent(item.id)}
                  user={item}
                />
              </Grid>
            ))}
          </Grid>
        </SortableContext>
        <SortableDragOverlay activeId={activeId} items={items.mainDash} />
      </DndContext>
    </Box>
  );
}

export default Assortment;
